'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

const RECENT_SEARCH_KEY = 'np:recent-searches';
const MAX_RECENT = 5;

const POPULAR_BRANDS = [
  { name: 'Lattafa',         slug: 'lattafa' },
  { name: 'YSL',             slug: 'ysl' },
  { name: 'Dolce & Gabbana', slug: 'd-g' },
  { name: 'Valentino DBIR',  slug: 'valentino-dbir' },
  { name: 'Lancôme',         slug: 'lancome' },
];

function readRecent(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_SEARCH_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function pushRecent(query: string) {
  if (typeof window === 'undefined') return;
  const cur = readRecent();
  const next = [query, ...cur.filter((q) => q !== query)].slice(0, MAX_RECENT);
  window.localStorage.setItem(RECENT_SEARCH_KEY, JSON.stringify(next));
}

export function HeaderSearch() {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState('');
  const [recent, setRecent] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const router  = useRouter();

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setRecent(readRecent());
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
  }, [open]);

  function go(href: string, recordQuery?: string) {
    if (recordQuery) pushRecent(recordQuery);
    router.push(href);
    setOpen(false);
    setQuery('');
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    go(`/search?q=${encodeURIComponent(q)}`, q);
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        aria-label="Open search"
        className="hover:text-ink"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="11" cy="11" r="7" />
          <line x1="16" y1="16" x2="21" y2="21" />
        </svg>
      </button>

      {open && (
        <div className="fixed inset-0 z-[110] bg-ink/45 backdrop-blur-sm flex items-start justify-center pt-24 px-6">
          <button
            type="button"
            aria-label="Close search"
            onClick={() => setOpen(false)}
            className="absolute inset-0 -z-10"
          />
          <div className="relative w-full max-w-2xl bg-bone shadow-2xl">
            <form onSubmit={submit} role="search">
              <div className="flex items-center px-6 py-4 border-b border-ink/10">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-strong mr-4 shrink-0">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="16" y1="16" x2="21" y2="21" />
                </svg>
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search fragrances or brands…"
                  className="flex-1 bg-transparent text-base text-ink placeholder:text-muted-strong focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="text-muted-strong hover:text-ink ml-4 shrink-0"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </button>
              </div>
            </form>

            <div className="px-6 py-6 space-y-6">
              {recent.length > 0 && (
                <div>
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-strong font-medium mb-3">Recent searches</p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => go(`/search?q=${encodeURIComponent(q)}`, q)}
                        className="text-xs uppercase tracking-[0.1em] px-3 py-1.5 border border-ink/20 hover:border-ink rounded-full text-ink/70 hover:text-ink transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-muted-strong font-medium mb-3">Popular brands</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {POPULAR_BRANDS.map((b) => (
                    <Link
                      key={b.slug}
                      href={`/brands/${b.slug}`}
                      onClick={() => setOpen(false)}
                      className="text-sm text-ink/75 hover:text-ink transition-colors"
                    >
                      {b.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-muted-strong font-medium mb-3">Quick links</p>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  <Link href="/discovery-set" onClick={() => setOpen(false)} className="text-sm text-ink/75 hover:text-ink transition-colors">Discovery Set</Link>
                  <Link href="/brands" onClick={() => setOpen(false)} className="text-sm text-ink/75 hover:text-ink transition-colors">All brands</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
